import { createEnv } from '@t3-oss/env-nextjs';
import * as z from 'zod';

const trimUrl = (url: unknown) => (typeof url === 'string' ? url.trim().replace(/\/$/, '') : url);
const url = () => z.preprocess(trimUrl, z.string().url());

export const env = createEnv({
    server: {
        ENV: z.enum(['prod', 'test', 'dev', 'ci']),
        CMS_API_INTERNAL_BASE_URL: url(),
        CMS_API_PUBLIC_BASE_URL: url(),
        CMS_PREVIEW_KEY: z.string(),
        APP_COMMIT: z.string().optional(),
        APP_BUILD: z.coerce.number().optional(),
    },
    client: {
        NEXT_PUBLIC_ENV: z.enum(['prod', 'test', 'dev', 'ci']),
        NEXT_PUBLIC_VERSION: z.string().default('0.0.1'),
        NEXT_PUBLIC_SITE_BASE_URL: url(),
        NEXT_PUBLIC_ENABLE_DEV_TOOLS: z.coerce.boolean().default(false),

        NEXT_PUBLIC_GOOGLE_MAPS_API_KEY: z.string().optional(),
        NEXT_PUBLIC_GOOGLE_MAP_ID: z.string().optional(),

        NEXT_PUBLIC_RENTALS_API_BASE_URL: url(),
        NEXT_PUBLIC_RENTALS_API_ACCOUNT_KEY: z.string(),

        NEXT_PUBLIC_JUCY_OBE_DIRECT_URL: url(),
        NEXT_PUBLIC_JUCY_BETA_OBE_DIRECT_URL: url().optional(),
        NEXT_PUBLIC_STAR_OBE_DIRECT_URL: url(),

        NEXT_PUBLIC_DD_APP_ID: z.string().optional(),
        NEXT_PUBLIC_DD_CLIENT_TOKEN: z.string().optional(),
        NEXT_PUBLIC_DD_SERVICE: z.string().optional(),
        NEXT_PUBLIC_DD_SITE: z.string().optional(),

        NEXT_PUBLIC_STORYBOOK_PREVIEW_ON_UNHANDLED_REQUEST: z.enum(['bypass', 'warn', 'error']).default('warn'),

        NEXT_PUBLIC_CHATBOT_PROVIDER: z.enum(['intercom', 'zendesk']).default('intercom'),
        NEXT_PUBLIC_INTERCOM_API_BASE_URL: z.string().optional(),
        NEXT_PUBLIC_INTERCOM_APP_ID: z.string().optional(),
        NEXT_PUBLIC_ZENDESK_WIDGET_KEY: z.string().optional(),

        NEXT_PUBLIC_ANALYTICS_MOCK_DATALAYER: z.coerce.boolean().default(false),
        NEXT_PUBLIC_ANALYTICS_DEBUG: z.coerce.boolean().default(false),

        NEXT_PUBLIC_ENABLE_LOCALE_POPUP: z.coerce.boolean().default(false),

        NEXT_PUBLIC_VWO_ACCOUNT_ID: z.string().optional(),
    },
    runtimeEnv: {
        ENV: process.env.ENV,
        CMS_API_INTERNAL_BASE_URL: process.env.CMS_API_INTERNAL_BASE_URL,
        CMS_API_PUBLIC_BASE_URL: process.env.CMS_API_PUBLIC_BASE_URL,
        CMS_PREVIEW_KEY: process.env.CMS_PREVIEW_KEY,
        APP_COMMIT: process.env.APP_COMMIT,
        APP_BUILD: process.env.APP_BUILD,

        NEXT_PUBLIC_GOOGLE_MAPS_API_KEY: process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY,
        NEXT_PUBLIC_GOOGLE_MAP_ID: process.env.NEXT_PUBLIC_GOOGLE_MAP_ID,

        NEXT_PUBLIC_ENV: process.env.NEXT_PUBLIC_ENV,
        NEXT_PUBLIC_VERSION: process.env.NEXT_PUBLIC_VERSION,

        NEXT_PUBLIC_SITE_BASE_URL: process.env.NEXT_PUBLIC_SITE_BASE_URL,
        NEXT_PUBLIC_ENABLE_DEV_TOOLS: process.env.NEXT_PUBLIC_ENABLE_DEV_TOOLS === 'true',

        NEXT_PUBLIC_RENTALS_API_BASE_URL: process.env.NEXT_PUBLIC_RENTALS_API_BASE_URL,
        NEXT_PUBLIC_RENTALS_API_ACCOUNT_KEY: process.env.NEXT_PUBLIC_RENTALS_API_ACCOUNT_KEY,

        NEXT_PUBLIC_JUCY_OBE_DIRECT_URL: process.env.NEXT_PUBLIC_JUCY_OBE_DIRECT_URL,
        NEXT_PUBLIC_JUCY_BETA_OBE_DIRECT_URL: process.env.NEXT_PUBLIC_JUCY_BETA_OBE_DIRECT_URL,
        NEXT_PUBLIC_STAR_OBE_DIRECT_URL: process.env.NEXT_PUBLIC_STAR_OBE_DIRECT_URL,

        NEXT_PUBLIC_DD_APP_ID: process.env.NEXT_PUBLIC_DD_APP_ID,
        NEXT_PUBLIC_DD_CLIENT_TOKEN: process.env.NEXT_PUBLIC_DD_CLIENT_TOKEN,
        NEXT_PUBLIC_DD_SERVICE: process.env.NEXT_PUBLIC_DD_SERVICE,
        NEXT_PUBLIC_DD_SITE: process.env.NEXT_PUBLIC_DD_SITE,
        NEXT_PUBLIC_STORYBOOK_PREVIEW_ON_UNHANDLED_REQUEST: process.env.NEXT_PUBLIC_STORYBOOK_PREVIEW_ON_UNHANDLED_REQUEST,

        NEXT_PUBLIC_CHATBOT_PROVIDER: process.env.NEXT_PUBLIC_CHATBOT_PROVIDER,
        NEXT_PUBLIC_INTERCOM_API_BASE_URL: process.env.NEXT_PUBLIC_INTERCOM_API_BASE_URL,
        NEXT_PUBLIC_INTERCOM_APP_ID: process.env.NEXT_PUBLIC_INTERCOM_APP_ID,
        NEXT_PUBLIC_ZENDESK_WIDGET_KEY: process.env.NEXT_PUBLIC_ZENDESK_WIDGET_KEY,

        NEXT_PUBLIC_ANALYTICS_MOCK_DATALAYER: process.env.NEXT_PUBLIC_ANALYTICS_MOCK_DATALAYER === 'true',
        NEXT_PUBLIC_ANALYTICS_DEBUG: process.env.NEXT_PUBLIC_ANALYTICS_DEBUG === 'true',

        NEXT_PUBLIC_ENABLE_LOCALE_POPUP: process.env.NEXT_PUBLIC_ENABLE_LOCALE_POPUP === 'true',

        NEXT_PUBLIC_VWO_ACCOUNT_ID: process.env.NEXT_PUBLIC_VWO_ACCOUNT_ID,
    },
    // Selecting a chatbot without its credentials would leave the site with no chat at all, so fail the
    // build rather than ship silently bot-free.
    createFinalSchema: (shape) =>
        z.object(shape).check((ctx) => {
            const { NEXT_PUBLIC_CHATBOT_PROVIDER, NEXT_PUBLIC_ZENDESK_WIDGET_KEY } = ctx.value;

            if (NEXT_PUBLIC_CHATBOT_PROVIDER === 'zendesk' && !NEXT_PUBLIC_ZENDESK_WIDGET_KEY) {
                ctx.issues.push({
                    code: 'custom',
                    input: NEXT_PUBLIC_ZENDESK_WIDGET_KEY,
                    path: ['NEXT_PUBLIC_ZENDESK_WIDGET_KEY'],
                    message: 'NEXT_PUBLIC_ZENDESK_WIDGET_KEY is required when NEXT_PUBLIC_CHATBOT_PROVIDER is zendesk',
                });
            }
        }),
});
